import clsx from 'clsx';
import * as React from 'react';
import { useSetRecoilState } from 'recoil';

import * as analytics from '@gitbook/app-analytics/client';
import { UserTheme } from '@gitbook/app-database';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch } from '@gitbook/hive-react';
import {
    createPalette,
    PaletteButton,
    PaletteCommandType,
    type PaletteDef,
    useConfigurationTheme,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button, ButtonGroup, DragKnob, Tooltip } from '@gitbook/spine-react';
import { useEventCallback, useLoadingState } from '@gitbook/util-react-hooks';
import { assertIsDefined, type MandateProps } from '@gitbook/util-ts';

import { useImageContentReference, useInsertFiles } from '../../../Components';
import { shortcuts } from '../../../Keyboard';
import { userThemeAtom } from '../../../state';
import defaultCoverUrl from './assets/default-page-cover.raw.svg';
import { useCoverPosition, useCoverResizer } from './Cover/hooks';

interface PageCoverProps {
    page: hiveCore.RevisionPageDocument;
    readOnly?: boolean;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
    rounded?: boolean;
}

// Height of the cover in pixels.
const DEFAULT_COVER_HEIGHT = 240;
/**
 * While we have min and max heights in px, the actual rendered height of a cover may differ. This is because we use object-fit: cover to ensure that if img uploads !== the cover's aspect ratio, we get no empty gaps.
 * For details, scroll to CoverResizer below
 */
const MIN_COVER_HEIGHT = 10;
const MAX_COVER_HEIGHT = 700;

function clampCoverHeight(value: number): number {
    return Math.min(MAX_COVER_HEIGHT, Math.max(MIN_COVER_HEIGHT, value));
}

/**
 * Cover for the page when one exists
 */
export function PageCover(props: PageCoverProps): React.ReactElement | null {
    const { page } = props;

    if (!page.cover) {
        return null;
    }

    const pageWithCover = page as MandateProps<hiveCore.RevisionPageDocument, 'cover'>;

    return (
        <React.Suspense fallback={<PageCoverFallback />}>
            <PageVisibleCover {...props} page={pageWithCover} />
        </React.Suspense>
    );
}

/**
 * Fallback when loading the cover.
 */
function PageCoverFallback(): React.ReactElement {
    return (
        <div className="relative w-full" style={{ height: DEFAULT_COVER_HEIGHT }}>
            <CoverLoading />
        </div>
    );
}

/**
 * Cover for the page when one exists
 */
function PageVisibleCover(
    props: PageCoverProps & {
        page: MandateProps<hiveCore.RevisionPageDocument, 'cover'>;
    }
): React.ReactElement {
    const { page, readOnly, rounded, analyticsContext } = props;
    const [repositioning, setRepositioning] = React.useState(false);
    const insertFiles = useInsertFiles();
    const hiveBranch = useHiveBranch();
    const [loading, wrapLoading] = useLoadingState();

    const resizeHandleRef = React.useRef<HTMLButtonElement>(null);
    const containerRef = React.useRef<HTMLDivElement | null>(null);

    // Resolve the cover reference to a URL
    const imageSource = useImageContentReference(page.cover.ref, page.cover.refDark);
    const coverUrl = imageSource?.uri ?? defaultCoverUrl;

    // Position of the cover in image pixels.
    const initialCoverY = page.cover.yPos || 0;
    const [coverY, setCoverY] = React.useState(initialCoverY);

    // Use useMemo to derive coverY from page.cover.yPos instead of useEffect
    const derivedCoverY = React.useMemo(() => page.cover.yPos || 0, [page.cover.yPos]);
    const positionHasChanged = coverY !== derivedCoverY;

    const handleSaveRepositioning = async () => {
        assertIsDefined(hiveBranch);

        setRepositioning(false);

        await hiveBranch.change((change) => {
            change.movePageCover(page.id, coverY);
        });

        analytics.logEvent({
            ...analyticsContext,
            type: 'edit_reposition_cover',
        });
    };

    const handleCancelRepositioning = () => {
        setRepositioning(false);
        setCoverY(derivedCoverY);
    };

    const pickCover = async (coverMode: 'light' | 'dark' | 'all') => {
        assertIsDefined(hiveBranch);

        const [ref] = await insertFiles.prompt({
            title: 'Select cover image',
            accept: 'images',
            acceptURL: true,
        });
        if (ref) {
            await wrapLoading(() =>
                hiveBranch.change((change) => {
                    if (coverMode === 'all') {
                        change.addPageCover(page.id, { ref, refDark: undefined });
                    } else {
                        change.addPageCover(page.id, {
                            [coverMode === 'dark' ? 'refDark' : 'ref']: ref,
                        });
                    }
                })
            );

            analytics.logEvent({
                ...analyticsContext,
                type: 'edit_upload_cover',
                coverMode,
            });
        }
    };

    const removeCover = async () => {
        assertIsDefined(hiveBranch);

        await wrapLoading(() =>
            hiveBranch.change((change) => {
                change.removePageCover(page.id);
            })
        );

        analytics.logEvent({
            ...analyticsContext,
            type: 'edit_remove_cover',
        });
    };

    const updateCoverSize = (coverSize: hiveCore.PageLayoutOptionsCoverSize) => {
        assertIsDefined(hiveBranch);

        hiveBranch.change((change) => {
            change.updatePageLayout(page.id, {
                coverSize,
            });
        });

        analytics.logEvent({
            ...analyticsContext,
            type: 'edit_cover_size',
            coverSize,
        });
    };

    const handleSaveResizing = useEventCallback((height: number) => {
        assertIsDefined(hiveBranch);
        hiveBranch.change((change) => change.setPageCoverHeight(page.id, height));

        analytics.logEvent({
            ...analyticsContext,
            type: 'edit_cover_height' as const,
            height,
        });
    });

    const { height, resizing } = useCoverResizer({
        resizeHandleRef,
        containerRef,
        value: clampCoverHeight(page.cover.height ?? DEFAULT_COVER_HEIGHT),
        min: MIN_COVER_HEIGHT,
        max: MAX_COVER_HEIGHT,
        readOnly,
        onChange: handleSaveResizing,
    });

    const paletteSource = () =>
        createPalette({
            groups: [
                {
                    key: 'themes',
                    commands: [
                        {
                            key: 'light',
                            icon: Icon.ThemeLight,
                            title: 'Set light mode cover',
                            trigger: () => {
                                pickCover('light');
                            },
                        },
                        {
                            key: 'dark',
                            icon: Icon.ThemeDark,
                            title: 'Set dark mode cover',
                            trigger: () => {
                                pickCover('dark');
                            },
                        },
                        {
                            key: 'all',
                            icon: Icon.Modes,
                            title: 'Set cover applied on both',
                            trigger: () => {
                                pickCover('all');
                            },
                        },
                    ],
                },
            ],
        });

    const palette = () => {
        const currentCoverSize = hiveCore.getPageLayoutOptions(page).coverSize;

        return createPalette({
            groups: [
                {
                    key: 'pageCover',
                    commands: [
                        {
                            key: 'pageCover.select',
                            icon: Icon.Replace,
                            title: 'Change image',
                            descendant: paletteSource,
                        },
                        {
                            key: 'pageCover.reposition',
                            icon: Icon.ImageSizeBlock,
                            title: 'Reposition',
                            trigger: () => {
                                setRepositioning(true);
                            },
                        },
                    ],
                },
                {
                    key: 'pageCoverSize',
                    commands: [
                        {
                            key: 'pageCoverSize.full',
                            icon: Icon.FullWidth,
                            title: 'Full width',
                            type: PaletteCommandType.CHECKBOX,
                            selected: currentCoverSize === hiveCore.PageLayoutOptionsCoverSize.Full,
                            trigger: () =>
                                updateCoverSize(hiveCore.PageLayoutOptionsCoverSize.Full),
                        },
                        {
                            key: 'pageCoverSize.hero',
                            icon: Icon.DefaultWidth,
                            title: 'Hero width',
                            type: PaletteCommandType.CHECKBOX,
                            selected: currentCoverSize === hiveCore.PageLayoutOptionsCoverSize.Hero,
                            trigger: () =>
                                updateCoverSize(hiveCore.PageLayoutOptionsCoverSize.Hero),
                        },
                    ],
                },
                {
                    key: 'pageCoverDanger',
                    commands: [
                        {
                            key: 'pageCoverDanger.remove',
                            icon: Icon.Trashcan,
                            title: 'Remove cover',
                            trigger: () => removeCover(),
                        },
                    ],
                },
            ],
        });
    };

    const isMultiThemeCover = Boolean(page.cover?.refDark);

    const mainEditButton = page.cover ? <CoverOptionsButton palette={palette} /> : null;

    const showCrosshair = resizing || repositioning;

    const disableResizing = true;

    return (
        <div
            className={clsx('group relative w-full flex-none', rounded && 'rounded-xl')}
            ref={containerRef}
            style={{ height, minHeight: height }}
        >
            <CoverRepositionableImage
                coverUrl={coverUrl}
                y={repositioning ? coverY : derivedCoverY}
                onChangeY={setCoverY}
                repositioning={repositioning}
            />
            {loading ? <CoverLoading /> : null}
            {showCrosshair ? <CoverCrosshair /> : null}
            {readOnly ? null : (
                <div
                    className={clsx(
                        'absolute right-4 bottom-4 flex gap-2 transition-opacity',
                        repositioning
                            ? 'opacity-100'
                            : 'opacity-0 group-hover:opacity-100 has-[[aria-expanded]]:opacity-100'
                    )}
                >
                    {repositioning ? (
                        <ButtonGroup>
                            <Button
                                label="Cancel"
                                kind="secondary"
                                onPress={handleCancelRepositioning}
                            />
                            <Button
                                label="Save"
                                kind="primary"
                                onPress={handleSaveRepositioning}
                                disabled={!positionHasChanged}
                            />
                        </ButtonGroup>
                    ) : (
                        <ButtonGroup>
                            {isMultiThemeCover ? <ThemeToggleButton /> : null}
                            {mainEditButton}
                        </ButtonGroup>
                    )}
                </div>
            )}
            {/* Will be available once the API, types, and GBO support are implemented */}
            {disableResizing || readOnly ? null : (
                <CoverResizer
                    coverHeight={height}
                    resizing={resizing}
                    resizeHandleRef={resizeHandleRef}
                />
            )}
        </div>
    );
}

export const CoverOptionsButton = React.memo(function CoverOptionsButton(props: {
    palette: () => PaletteDef;
}) {
    const { palette } = props;
    return (
        <PaletteButton
            label="Cover options"
            leadingIcon={Icon.Image}
            title="Edit image, position, and more"
            kind="secondary"
            palette={palette}
        />
    );
});

function ThemeToggleButton() {
    const theme = useConfigurationTheme();
    const setUserTheme = useSetRecoilState(userThemeAtom);

    const isLightMode = theme.color === 'light';
    const oppositeTheme = theme.color === 'light' ? 'dark' : 'light';

    return (
        <Button
            kind="secondary"
            leadingIcon={isLightMode ? Icon.ThemeDark : Icon.ThemeLight}
            label={`Toggle theme to view ${oppositeTheme} cover.`}
            onPress={() => {
                setUserTheme(isLightMode ? UserTheme.Dark : UserTheme.Light);
            }}
            iconOnly
        />
    );
}

/**
 * Image for the cover with animation and pan responder.
 */
function CoverRepositionableImage(props: {
    coverUrl: string;
    y: number;
    onChangeY: (y: number) => void;
    repositioning: boolean;
}): React.ReactElement | null {
    const { coverUrl, y, onChangeY, repositioning } = props;

    const imgRef = React.useRef<HTMLImageElement | null>(null);

    const { containerRef, objectPositionY, positionLabel, setImageDimensions } = useCoverPosition({
        imgRef,
        value: y,
        repositioning,
        onChange: onChangeY,
    });

    const ready = !!imgRef.current;

    return (
        <Tooltip label={positionLabel} isOpen={repositioning}>
            <div
                ref={containerRef}
                className={clsx('absolute inset-0 overflow-hidden', repositioning && 'cursor-grab')}
                {...(ready ? { 'data-testid': 'page.cover' } : {})}
            >
                <img
                    ref={imgRef}
                    src={coverUrl}
                    alt="Page cover"
                    className="h-full w-full object-cover"
                    style={{
                        objectPosition: `50% ${objectPositionY}%`,
                    }}
                    onLoad={(event) => {
                        setImageDimensions({
                            width: event.currentTarget.naturalWidth,
                            height: event.currentTarget.naturalHeight,
                        });
                    }}
                />
            </div>
        </Tooltip>
    );
}

/**
 * Content of the cover when loading.
 */
function CoverLoading(): React.ReactElement {
    return <div className="shimmer absolute inset-0 flex items-center justify-center bg-muted" />;
}

export const CoverResizer = React.memo(function CoverResizer(props: {
    resizeHandleRef: React.RefObject<HTMLButtonElement>;
    resizing: boolean;
    coverHeight: number;
}): React.ReactElement {
    const { resizeHandleRef, coverHeight, resizing } = props;
    const [isDragHandleFocused, setIsDragHandleFocused] = React.useState(false);

    // Calculate percentage of the height range
    const heightPercentage = Math.round(
        ((coverHeight - MIN_COVER_HEIGHT) / (MAX_COVER_HEIGHT - MIN_COVER_HEIGHT)) * 100
    );
    const isAtMin = coverHeight === MIN_COVER_HEIGHT;

    const resizingLabel = resizing ? `${heightPercentage}%` : 'Drag to resize';

    const tooltipProps = resizing
        ? {
              label: isAtMin ? 'Open cover options to remove the cover' : resizingLabel,
          }
        : { label: 'Drag, or resize with keys', shortcut: shortcuts.EDITOR_RESIZE_COVER };

    const showTooltip = resizing || isDragHandleFocused;

    return (
        <Tooltip {...tooltipProps} isOpen={showTooltip}>
            <div className="-bottom-3 absolute inset-x-0 z-20 flex justify-center">
                <DragKnob
                    ref={resizeHandleRef}
                    tabIndex={0}
                    className="group/knob relative h-6 w-full cursor-row-resize rounded"
                    isDragging={resizing}
                    onFocus={() => setIsDragHandleFocused(true)}
                    onBlur={() => setIsDragHandleFocused(false)}
                >
                    <div
                        className={clsx(
                            '-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 h-0.5 w-full rounded-full bg-transparent transition-colors',
                            resizing
                                ? 'bg-[var(--sp-color-button-drag-bg-active)]'
                                : 'group-hover/knob:bg-[var(--sp-color-button-drag-bg-default)] group-focus/knob:bg-[var(--sp-color-button-drag-bg-default)]'
                        )}
                    />
                </DragKnob>
            </div>
        </Tooltip>
    );
});

export const CoverCrosshair = React.memo(function CoverCrosshair(): React.ReactElement {
    const dash = 19; // dash length, keep uneven for correct centering
    const gap = 12; // gap between dashes
    const tile = dash + gap; // tile size is just the sum of the dash and the gap

    return (
        <>
            {/* Vertical dashed line */}
            <div
                className="-translate-x-1/2 absolute top-0 left-1/2 h-full w-px bg-repeat-y mix-blend-overlay"
                style={{
                    backgroundImage: `linear-gradient(to bottom, rgba(255,255,255,0.75) 0 ${dash}px, transparent ${dash}px ${tile}px )`,
                    backgroundSize: `100% ${tile}px`,
                    // Ensures the dashes always overlap in the center of the crosshair to form a `+`.
                    backgroundPosition: `center calc(50% + ${gap / 2}px)`,
                }}
            />

            {/* Horizontal dashed line */}
            <div
                className="-translate-y-1/2 absolute top-1/2 left-0 h-px w-full bg-repeat-x mix-blend-overlay"
                style={{
                    backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.75) 0 ${dash}px, transparent ${dash}px ${tile}px )`,
                    backgroundSize: `${tile}px 100%`,
                    backgroundPosition: `calc(50% + ${gap / 2}px) center`,
                }}
            />
        </>
    );
});
